import { t } from "@lingui/macro";
import { ArrowRight, Crown, Lightning, Star, Users } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@stylingcv/ui";
import { useNavigate } from "react-router-dom";

import { useUserAccess } from "@/client/hooks/use-user-access";

type ResumeLimitModalProps = {
  isOpen: boolean;
  onClose: () => void;
  errorMessage?: string;
  currentCount?: number;
  maxAllowed?: number;
};

export const ResumeLimitModal = ({
  isOpen,
  onClose,
  errorMessage,
  currentCount = 2,
  maxAllowed = 2,
}: ResumeLimitModalProps) => {
  const navigate = useNavigate();
  const { userType, isGuest } = useUserAccess();

  const handleUpgrade = () => {
    onClose();
    // For guests, navigate to registration page instead of pricing
    if (isGuest) {
      navigate("/auth/register");
    } else {
      navigate("/pricing");
    }
  };

  const getUpgradeMessage = () => {
    if (isGuest) {
      return t`Sign up for free to create more resumes`;
    }

    switch (userType) {
      case "FREE": {
        return t`Upgrade to Basic plan to create up to 5 resumes`;
      }
      case "BASIC": {
        return t`Upgrade to Professional to create up to 10 resumes`;
      }
      default: {
        return t`Upgrade your plan to create more resumes`;
      }
    }
  };

  const getTitle = () => {
    if (isGuest) {
      return t`Resume Limit Reached`;
    }
    return t`Upgrade to Create More Resumes`;
  };

  const getFeatures = () => {
    if (isGuest) {
      return [
        t`Create up to 2 resumes`,
        t`AI-powered resume generation`,
        t`Professional templates`,
        t`Export to PDF`,
      ];
    }

    if (userType === "FREE") {
      return [
        t`Create up to 5 resumes`,
        t`Unlimited AI features`,
        t`Premium templates`,
        t`Advanced ATS optimization`,
      ];
    }

    return [
      t`Create up to 10 resumes`,
      t`Unlimited everything`,
      t`Priority support`,
      t`Custom branding`,
    ];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Crown className="size-5 text-primary" weight="fill" />
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="pt-1.5 text-sm text-muted-foreground">
            {isGuest
              ? t`You've reached your guest limit of ${maxAllowed} resume. Sign up for free to create more!`
              : t`You've created ${currentCount} out of ${maxAllowed} resumes. Upgrade to continue creating professional resumes.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-3">
          {/* Features List */}
          <div className="space-y-3">
            <h4 className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Star className="size-4 text-primary" />
              {isGuest ? t`Free Account Benefits` : t`Upgrade Benefits`}
            </h4>
            <ul className="grid gap-2.5 sm:grid-cols-2">
              {getFeatures().map((feature, index) => (
                <li
                  key={index}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <div className="size-1.5 rounded-full bg-primary/40" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Social Proof */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="size-4" />
            <span>{t`Join 5 million+ professionals who upgraded`}</span>
          </div>

          {/* Error Message Display */}
          {errorMessage && (
            <div className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2.5">
              <p className="text-sm font-medium text-destructive">{errorMessage}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="ghost" className="w-full sm:w-auto" onClick={onClose}>
            {t`Maybe Later`}
          </Button>

          <Button className="w-full sm:w-auto" onClick={handleUpgrade}>
            {isGuest ? (
              <>
                <Lightning className="mr-2 size-4" />
                {t`Sign Up Free`}
              </>
            ) : (
              <>
                <Crown className="mr-2 size-4" />
                {t`Upgrade Now`}
              </>
            )}
            <ArrowRight className="ml-2 size-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
